import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { ROLE_LABELS } from "../lib/constants";
import { Briefcase, ListTodo, AlertTriangle, CheckCircle2, Clock, ScrollText } from "lucide-react";
import { Link } from "react-router-dom";

export default function Profile() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/profile/summary").then((r) => setData(r.data)).catch(console.error);
  }, []);

  if (!data) return <div className="text-stone-500">Loading profile...</div>;
  const { user, stats, projects, tasks, recent_activity } = data;
  const initials = user?.name?.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "U";

  return (
    <div className="space-y-6 animate-fade-up" data-testid="profile-page">
      {/* Header card */}
      <div className="bg-white border border-stone-200 rounded-2xl p-6 flex flex-col md:flex-row md:items-center gap-5">
        <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shrink-0" style={{ background: "linear-gradient(135deg,#72BE44,#145353)" }} data-testid="profile-avatar">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-stone-900">{user.name}</h1>
          <div className="text-sm text-stone-500">{user.email}</div>
          <div className="flex flex-wrap gap-2 mt-2">
            <Badge>{ROLE_LABELS[user.role] || user.role}</Badge>
            {user.designation && <Badge tone="dark">{user.designation}</Badge>}
            {user.department && <Badge tone="grey">{user.department} Dept.</Badge>}
            {user.employee_id && <Badge tone="grey">ID: {user.employee_id}</Badge>}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold flex items-center gap-1 justify-end"><Clock className="w-3 h-3" /> Last Login</div>
          <div className="text-sm font-semibold text-stone-700 mt-0.5" data-testid="profile-last-login">
            {user.last_login_at ? new Date(user.last_login_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
          </div>
          {user.joining_date && <div className="text-[11px] text-stone-400 mt-1">Joined {user.joining_date}</div>}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="profile-stats">
        <Stat icon={Briefcase} label="Assigned Projects" value={stats.assigned_projects} />
        <Stat icon={ListTodo} label="Pending Activities" value={stats.open_tasks} tone="#B45309" />
        <Stat icon={AlertTriangle} label="Overdue" value={stats.overdue_tasks} tone={stats.overdue_tasks ? "#BE123C" : "#72BE44"} />
        <Stat icon={CheckCircle2} label="Completed" value={stats.completed_tasks} tone="#72BE44" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Assigned projects */}
        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <div className="text-sm font-bold mb-3" style={{ color: "#145353" }}>My Projects</div>
          <div className="space-y-2" data-testid="profile-projects">
            {projects.length === 0 && <div className="text-sm text-stone-400 py-4 text-center">No projects assigned via tasks yet</div>}
            {projects.map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`} className="block px-3 py-2.5 rounded-lg border border-stone-100 hover:border-[#72BE44] transition">
                <div className="text-sm font-medium text-stone-800 truncate">{p.project_name}</div>
                <div className="text-[11px] text-stone-400 truncate">{p.client_name} · {p.service_type} · {p.current_stage || p.status}</div>
              </Link>
            ))}
          </div>
        </div>

        {/* Pending tasks */}
        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold" style={{ color: "#145353" }}>My Pending Tasks</div>
            <Link to="/tasks" className="text-xs font-semibold hover:underline" style={{ color: "#72BE44" }}>All tasks →</Link>
          </div>
          <div className="space-y-1.5" data-testid="profile-tasks">
            {tasks.length === 0 && <div className="text-sm text-stone-400 py-4 text-center">All clear! 🎉</div>}
            {tasks.map((t) => (
              <div key={t.id} className="px-3 py-2 rounded-lg bg-stone-50 border border-stone-100">
                <div className="text-sm text-stone-800 truncate">{t.title}</div>
                <div className="text-[11px] text-stone-400">{t.status}{t.due_date ? ` · due ${t.due_date.slice(0, 10)}` : ""}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent activity */}
        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <div className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: "#145353" }}><ScrollText className="w-4 h-4" /> My Recent Activity</div>
          <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="profile-activity">
            {recent_activity.length === 0 && <div className="text-sm text-stone-400 py-4 text-center">No recorded activity yet</div>}
            {recent_activity.map((a) => (
              <div key={a.id} className="text-xs border-l-2 pl-3 py-0.5" style={{ borderColor: "#72BE44" }}>
                <div className="text-stone-700 line-clamp-2">{a.summary}</div>
                <div className="text-[10px] text-stone-400 mt-0.5">{new Date(a.at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const Badge = ({ children, tone = "green" }) => {
  const style = {
    green: { background: "rgba(114,190,68,0.12)", color: "#145353" },
    dark: { background: "rgba(20,83,83,0.1)", color: "#145353" },
    grey: { background: "#f5f5f4", color: "#58595B" },
  }[tone];
  return <span className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full" style={style}>{children}</span>;
};

const Stat = ({ icon: Icon, label, value, tone = "#145353" }) => (
  <div className="bg-white border border-stone-200 rounded-xl p-4 flex items-center gap-3">
    <Icon className="w-5 h-5 shrink-0" style={{ color: tone }} />
    <div>
      <div className="text-2xl font-bold leading-none text-stone-900">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mt-1">{label}</div>
    </div>
  </div>
);
